import React, { useState } from 'react';
import QRCode from 'react-qr-code';
import md5 from 'md5';
import { Alert, Form, Col, Row, Button, Card, Spinner } from 'react-bootstrap';
import api from '../../services/api';
import AppFooter from '../../components/AppFooter';

const INITIAL_FORM = {
  codAtestado: '',
  codPaciente: '',
  cpfUpdate: '',
  origem: '',
};

export default function ValidaAtestado() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [validated, setValidated] = useState(false);
  const [status, setStatus] = useState({ kind: 'idle' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isDisabled = isSubmitting || status.kind === 'success';

  const handleClickICM = event => {
    event.preventDefault();
    window.location.href = 'https://www.icm.com.br';
  };

  const handleChange = field => event => {
    const raw = event.target.value;
    const value = field === 'cpfUpdate' ? raw.replace(/\D/g, '') : raw;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const formEl = event.currentTarget;

    setValidated(true);

    if (!formEl.checkValidity()) {
      event.stopPropagation();
      return;
    }

    setIsSubmitting(true);
    setStatus({ kind: 'idle' });

    try {
      const result = await api.put(`/paciente/atestado/${form.codAtestado}`, {
        status: 'Validado',
        origem: form.origem,
        cpfUpdate: form.cpfUpdate,
        codPaciente: form.codPaciente,
      });

      setStatus({
        kind: 'success',
        message: result?.data?.message ?? 'Atestado validado com sucesso!',
        atestado: result?.data?.atestado ?? null,
      });
    } catch (err) {
      const serverMessage = err?.response?.data?.message;
      const serverAtestado = err?.response?.data?.atestado ?? null;

      if (serverMessage) {
        setStatus({ kind: 'error', message: serverMessage, atestado: serverAtestado });
        return;
      }

      if (err?.request && !err?.response) {
        setStatus({
          kind: 'error',
          message: 'Erro no servidor! Verifique sua conexão.',
          atestado: null,
        });
        return;
      }

      setStatus({
        kind: 'error',
        message: 'Erro ao validar o atestado!',
        atestado: null,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const authHash = status.atestado
    ? md5(JSON.stringify(status.atestado.DESCRICAO))
    : null;

  return (
    <Row className="justify-content-md-center mb-1 mt-5">
      <Col md="auto">
        <Card border="success" className="text-center">
          <Card.Header>
            <Card.Title className="text-success">
              <strong>VALIDAÇÃO DE ATESTADO</strong>
            </Card.Title>
            {status.kind === 'success' && (
              <>
                <Alert variant="success">{status.message}</Alert>
                {authHash && <Card.Text>Autenticação: {authHash}</Card.Text>}
              </>
            )}
            {status.kind === 'error' && (
              <>
                <Alert variant="danger">{status.message}</Alert>
                {authHash && <Card.Text>Autenticação: {authHash}</Card.Text>}
              </>
            )}
          </Card.Header>

          <Card.Body>
            <Card.Text className="text-success">
              <strong>Informe os dados abaixo para VALIDAR o Atestado!</strong>
            </Card.Text>
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
              <Form.Row>
                <Form.Group as={Col} sm>
                  <Form.Label htmlFor="inputAtestado">
                    <strong>CÓDIGO DO ATESTADO</strong>
                  </Form.Label>
                  <Form.Control
                    required
                    type="text"
                    id="inputAtestado"
                    name="codAtestado"
                    value={form.codAtestado}
                    onChange={handleChange('codAtestado')}
                    disabled={isDisabled}
                    aria-describedby="atestadoHelp"
                  />
                  <Form.Text id="atestadoHelp" muted>
                    <div className="text-primary">
                      Digite o Código do Atestado.
                    </div>
                  </Form.Text>
                </Form.Group>
                <Form.Group as={Col} sm>
                  <Form.Label htmlFor="inputPaciente">
                    <strong>CÓDIGO DO PACIENTE</strong>
                  </Form.Label>
                  <Form.Control
                    required
                    type="text"
                    id="inputPaciente"
                    name="codPaciente"
                    value={form.codPaciente}
                    onChange={handleChange('codPaciente')}
                    disabled={isDisabled}
                    aria-describedby="pacienteHelp"
                  />
                  <Form.Text id="pacienteHelp" muted>
                    <div className="text-primary">
                      Digite o Código do Paciente.
                    </div>
                  </Form.Text>
                </Form.Group>
              </Form.Row>
              <Form.Row>
                <Form.Group as={Col} sm>
                  <Form.Label htmlFor="inputCPF">
                    <strong>CPF DO PROFISSIONAL</strong>
                  </Form.Label>
                  <Form.Control
                    required
                    type="text"
                    id="inputCPF"
                    name="cpfUpdate"
                    maxLength={11}
                    inputMode="numeric"
                    value={form.cpfUpdate}
                    onChange={handleChange('cpfUpdate')}
                    disabled={isDisabled}
                    aria-describedby="cpfHelp"
                  />
                  <Form.Text id="cpfHelp" muted>
                    <div className="text-primary">
                      Informe o CPF do Profissional que está realizando a
                      validação do Atestado.
                    </div>
                  </Form.Text>
                </Form.Group>
              </Form.Row>
              <Form.Row>
                <Form.Group as={Col} sm>
                  <Form.Label
                    htmlFor="inputProfissional"
                    className="font-weight-bold"
                  >
                    NOME DO PROFISSIONAL
                  </Form.Label>
                  <Form.Control
                    required
                    type="text"
                    id="inputProfissional"
                    name="origem"
                    value={form.origem}
                    onChange={handleChange('origem')}
                    disabled={isDisabled}
                    aria-describedby="profissionalHelp"
                  />
                  <Form.Text id="profissionalHelp" muted>
                    <div className="text-primary">
                      Informe o Nome do Profissional que está validando o
                      Atestado.
                    </div>
                  </Form.Text>
                </Form.Group>
              </Form.Row>
              <Form.Group as={Col} sm className="text-center">
                <Button type="submit" size="lg" disabled={isDisabled}>
                  {isSubmitting ? (
                    <Spinner animation="border" size="sm" role="status" />
                  ) : (
                    'Validar'
                  )}
                </Button>{' '}
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={handleClickICM}
                  disabled={isSubmitting}
                >
                  Fechar
                </Button>
              </Form.Group>
            </Form>
          </Card.Body>
          <Card.Footer className="text-muted">
            <QRCode size={50} value="https://www.icm.com.br" />
            <AppFooter />
          </Card.Footer>
        </Card>
      </Col>
    </Row>
  );
}
